import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import HeaderWithChildrenWrapper from 'shared/components/molecules/HeaderWithChildrenWrapper';
import { useDispatch, useSelector } from 'react-redux';
import { selectCompanyData } from 'User/selectors';
import { hasSSEAdminRole, hasSSEAccountManagerRole, hasSSEAdminWildcardRole } from 'User/selectors';
import { getCease, setActiveCustomer } from '../actions';
import { selectCeaseErrorMessage, selectCeaseGetState, selectCeaseSelected } from '../selectors';
import { RequestLifecycle } from 'shared/actions';
import LinkButton from 'shared/components/atoms/LinkButton';
import { ButtonMainStyle } from 'shared/components/atoms/Button';
import Alert, { Alerts } from 'shared/components/atoms/Alert';
import SpinnerWithText from 'shared/components/molecules/SpinnerWithText';
import ReadOnlyCard from 'shared/components/organisms/ReadOnlyCard';
import { NotesFieldSummary, TextWithLabelSummary } from 'Order/OrderBuilder/shared/components/SummaryTextHelper';
import { pages, useTrackPage } from 'shared/utils/trackPage';
import { getCustomerFromCompanyData } from 'CustomerRequests/utils/getCustomerFromCompanyData';
import { ceaseList, submitCease } from 'CustomerRequests/routes';
import { buildIncludedIndex, getSubmittedByDisplayName } from '../utils/includedIndex';
import dayjs from 'dayjs';
import { getTypeLabelFromCategory } from '../utils/requestType';

const CeaseDetails: FC = () => {
  useTrackPage(pages.ceaseRequestDetails);

  const navigate = useNavigate();
  const location = useLocation();
  const [showCreatedSuccess, setShowCreatedSuccess] = useState(false);

  const { id } = useParams();

  const dispatch = useDispatch();
  const companyData = useSelector(selectCompanyData);
  const { customerId: globalCustomerId } = getCustomerFromCompanyData(companyData);
  const navigationState = location.state as { customerId?: string; ceaseCreated?: boolean } | null;
  const customerId = navigationState?.customerId || globalCustomerId;
  const isSSEAdmin = useSelector((state: any) => hasSSEAdminRole(state) || hasSSEAccountManagerRole(state) || hasSSEAdminWildcardRole(state));

  const previousCustomerId = useRef<string | null>(null);
  const initialGlobalCustomerId = useRef<string | null>(null);
  const isFromAllCustomersMode = !!navigationState?.customerId;

  const selected = useSelector(selectCeaseSelected);
  const getState = useSelector(selectCeaseGetState);
  const errorMessage = useSelector(selectCeaseErrorMessage);

  const record = useMemo(() => {
    if (selected && id && selected.id === id) return selected;
    return null;
  }, [id, selected]);

  const includedIndex = useMemo(() => buildIncludedIndex(record?.included || []), [record?.included]);
  const submittedByDisplayName = record ? getSubmittedByDisplayName(record, includedIndex) : '';

  useEffect(() => {
    if (!customerId || !id) return;

    previousCustomerId.current = customerId;
    dispatch(setActiveCustomer(customerId));
    if (record) return;
    dispatch(getCease({ customerId, id }));
  }, [customerId, dispatch, id, record]);

  // If viewing from "all customers" mode, detect when user manually changes the global customer selector
  useEffect(() => {
    if (!isFromAllCustomersMode || !id) return;

    // Initialize on first run
    if (initialGlobalCustomerId.current === null) {
      initialGlobalCustomerId.current = globalCustomerId;
      return;
    }

    // If the user changed the global customer selector, go back to the list
    if (initialGlobalCustomerId.current !== globalCustomerId) {
      navigate(ceaseList, { replace: true });
    }
  }, [globalCustomerId, isFromAllCustomersMode, id, navigate]);

  useEffect(() => {
    if (!navigationState?.ceaseCreated) return;

    setShowCreatedSuccess(true);
    navigate('.', { replace: true, state: { customerId } });
  }, [navigationState?.ceaseCreated, navigate, customerId]);

  const isLoading = getState === RequestLifecycle.Loading && !record;
  const displayReference = record?.snowReference ?? "Pending";
  const hasCompletedLoad = getState === RequestLifecycle.Success || getState === RequestLifecycle.Error;

  const formatDate = (value: string) => {
    if (!value) return '';
    const d = dayjs(value);
    return d.isValid() ? d.format('DD/MM/YYYY') : value;
  };

  const onCopy = () => {
    if (!record) return;
    navigate(submitCease, {
      state: {
        copyFromCease: record,
        customerId: record.customerId,
        customerName: record.customerName,
      },
    });
  };

  return (
    <HeaderWithChildrenWrapper
      header={displayReference ? `Cease request ${displayReference}` : 'Cease request'}
      subHeader={
        record
          ? `Submitted ${formatDate(record.submittedAt)}${submittedByDisplayName ? ` by ${submittedByDisplayName}` : ''}`
          : undefined
      }
      headerCopyToClipboardText={displayReference}
    >
      <Container>
        <div className="mb-3 details-actions">
          
          <LinkButton pathname={ceaseList} buttonText="Back to cease requests" mainStyle={ButtonMainStyle.LinkPrimary} />
          
          <button
            type="button"
            className="copy-btn"
            onClick={onCopy}
            disabled={!record}
          >
            Copy
          </button>

        </div>

        {showCreatedSuccess && (
          <div className="mb-3">
            <Alert alertType={Alerts.SUCCESS}>
              Your cease request has been successfully submitted. Neos Cease team will be in touch within 48 hours, 
              the title of this communication will contain the Cease reference relating to your cease request.
            </Alert>
          </div>
        )}

        {errorMessage && <Alert alertType={Alerts.DANGER}>{errorMessage}</Alert>}

        {isLoading && <SpinnerWithText text="Loading cease request" size="large" />}

        {hasCompletedLoad && !record && <Alert>Unable to load this cease request.</Alert>}

        {record && (
          <>
            <div className="cards">
              <ReadOnlyCard title="Cease request details" className='card'>
                <ul className="list-unstyled mb-0">
                  <li>
                    <TextWithLabelSummary label="Cease reference" text={displayReference} />
                  </li>
                  <li>
                    <TextWithLabelSummary
                      label="Request type"
                      text={getTypeLabelFromCategory(record.category, record.subCategory)}
                    />
                  </li>
                  <li>
                    <TextWithLabelSummary label="Customer reference" text={record.customerReference || 'Not provided'} />
                  </li>
                  {isSSEAdmin && (
                    <>
                      <li>
                        <TextWithLabelSummary label="Status" text={record.status} />
                      </li>                
                      <li>
                        <TextWithLabelSummary label="Integration status" text={record.integrationStatus || 'Not synced'} />
                      </li>
                    </>
                  )}
                </ul>
              </ReadOnlyCard>

              <ReadOnlyCard title="Customer details" className='card'>
                <ul className="list-unstyled mb-0">
                  <li>
                    <TextWithLabelSummary label="Customer name" text={record.customerName} />
                  </li>
                  <li>
                    <TextWithLabelSummary label="Submitted by" text={submittedByDisplayName} />
                  </li>
                </ul>
              </ReadOnlyCard>

              <ReadOnlyCard title="Primary contact" className='card'>
                <ul className="list-unstyled mb-0">
                  <li>
                    <TextWithLabelSummary label="Name" text={record.primaryContactName} />
                  </li>
                  <li>
                    <TextWithLabelSummary label="Email" text={record.primaryContactEmail} />
                  </li>
                  <li>
                    <TextWithLabelSummary label="Phone" text={record.primaryContactPhone} />
                  </li>
                </ul>
              </ReadOnlyCard>

              <ReadOnlyCard title="Secondary contact" className='card'>
                <ul className="list-unstyled mb-0">
                  <li>
                    <TextWithLabelSummary label="Email" text={record.secondaryContactEmail || 'Not provided'} />
                  </li>
                </ul>
              </ReadOnlyCard>

              <ReadOnlyCard title="Service details" className='card'>
                <ul className="list-unstyled mb-0">
                  <li>
                    <TextWithLabelSummary label="Customer reference" text={record.customerReference || 'Not provided'} />
                  </li>
                  <li>
                    <TextWithLabelSummary label="IDN" text={record.idn} />
                  </li>
                  <li>
                    <TextWithLabelSummary label="Cease reason" text={record.ceaseReason} />
                  </li>
                  <li>
                    <TextWithLabelSummary label="Requested cease date" text={formatDate(record.requestedCeaseDate)} />
                  </li>
                  <li>
                    <NotesFieldSummary label="Additional details" text={record.details || 'Not provided'} className="recordDetails" maxLength={1000} />
                  </li>
                </ul>
              </ReadOnlyCard>
            </div>
          </>
        )}
      </Container>
    </HeaderWithChildrenWrapper>
  );
};

const Container = styled.div`
  .details-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .copy-btn {
    border: none;
    background: transparent;
    text-decoration: underline;
    padding: 0;
    margin: 0;
    font-family: ${(props) => props.theme.typography.fontFamilyBold};
    color: ${(props) => props.theme.colours.primaryC1};
    cursor: pointer;
  }

  .copy-btn[disabled] {
    cursor: not-allowed;
    opacity: 0.4;
  }

  .cards {
    display: flex;
    flex-direction: column;
    row-gap: 1rem;
  }

  .card {
    padding: 1.3em;
  }

  .card .content {
    padding-top: 1em;
  }

  /* TextWithLabelSummary defaults values to right-aligned; for cease details we want a simpler left-aligned readout */
  .label, .notesLabel {
    flex: 0 0 300px;
    max-width: 300px;
  }

  .value, .notesValue {
    flex: 1;
    text-align: left;
  }

  .recordDetails {
    display: flex;
  }
`;

export default CeaseDetails;
